import React, { FC, useEffect, useState } from 'react';
import {
  Flex,
  Modal,
  Button,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  Text,
  useTheme,
  Heading,
  Grid,
} from '@chakra-ui/react';
import { Form, Formik, FormikProps } from 'formik';
import FormikInput from 'components/primitives/FormikInput';
import { getInitialValues, validationSchema } from './EditQALogModal.utils';
import {
  EditQALogModalViewProps,
  FormValues,
  QAItem,
} from './EditQALogModal.props';
import FormikDatepicker from 'components/primitives/FormikDatepicker';
import SkuMultiSelect from 'components/primitives/SkuMultiSelect';
import { useQuery } from 'react-query';
import { fetchEmployees } from 'api/employees';
import { SKU } from 'types/sku';
import FormikTextArea from 'components/primitives/FormikTextArea';
import { EmployeeFilterableSelect } from '../EmployeeSelect';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
  noOfLines: 1,
  marginBottom: '4px',
  marginLeft: '16px',
  fontWeight: '500',
};

const labelTextareaProps = {
  fontSize: '16px',
  color: 'support.500',
  noOfLines: 1,
  marginBottom: '4px',
  fontWeight: '500',
};

const css = {
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    width: '10px',
    height: '10px',
    background: 'primary.900',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'primary.400',
    borderRadius: '24px',
  },
};

const EditQALogModalView: FC<EditQALogModalViewProps> = (props) => {
  const {
    isOpen,
    onClose,
    onSubmit,
    onDelete,
    isLoading,
    isLoadingSKU,
    skus,
    qALog,
  } = props;

  const theme = useTheme();
  const queryKey = 'fetch-super-admins';
  const queryFn = () =>
    fetchEmployees({
      washHub: undefined,
      role: 'super_admin',
    } as any);

  const options = {
    keepPreviousData: true,
  };
  const { data } = useQuery(queryKey, queryFn, options);
  const pc = theme?.colors?.primary['800'];
  const employees = data?.items || [];

  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);

  const handleSkuChange = (
    selectedSkus: string[],
    setFieldValue: any,
    currentItems: QAItem[],
  ) => {
    setSelectedSkus(selectedSkus);

    const currentItemsMap = currentItems.reduce((acc, item) => {
      acc[item.sku] = item;
      return acc;
    }, {} as Record<string, QAItem>);

    const newItems: QAItem[] = selectedSkus.map((sku) => {
      const existingItem = currentItemsMap[sku] || {};
      return {
        sku,
        passed:
          existingItem.passed !== undefined ? existingItem.passed : undefined,
        dirty:
          existingItem.dirty !== undefined ? existingItem.dirty : undefined,
        damaged:
          existingItem.damaged !== undefined ? existingItem.damaged : undefined,
        total:
          existingItem.total !== undefined ? existingItem.total : undefined,
        yield:
          existingItem.yield !== undefined ? existingItem.yield : undefined,
      };
    });

    setFieldValue('items', newItems);
  };

  useEffect(() => {
    if (qALog) {
      const defaultSkus = skus
        .filter((item: SKU) =>
          qALog.items.some(
            (logItem: { sku: string }) => logItem.sku === item?._id,
          ),
        )
        .map((item: SKU) => item._id);

      setSelectedSkus(defaultSkus);
    }
  }, [qALog, skus]);

  return (
    <Modal
      size="6xl"
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="30px">
        <Flex
          px="50px"
          shadow="lg"
          position="relative"
          justify="space-between"
          align="center">
          <Heading fontSize="24px" fontWeight="600" my="20px">
            Update QA Log
          </Heading>
          <Flex>
            <Button
              mr="4"
              colorScheme="red"
              background="red"
              onClick={onDelete}>
              Delete
            </Button>
          </Flex>
          <ModalCloseButton _focus={{ outline: 'none' }} top="4" />
        </Flex>

        <ModalBody>
          <Flex direction="column" px={6} pb={6}>
            <Formik
              onSubmit={onSubmit}
              initialValues={getInitialValues(qALog)}
              validationSchema={validationSchema}>
              {({ values, setFieldValue }: FormikProps<FormValues>) => {
                const totals = values?.items?.reduce(
                  (acc, item) => {
                    const passed = Number(item?.passed || 0);
                    const dirty = Number(item?.dirty || 0);
                    const damaged = Number(item?.damaged || 0);

                    acc.passed += passed;
                    acc.dirty += dirty;
                    acc.damaged += damaged;
                    acc.total += passed + dirty + damaged;

                    return acc;
                  },
                  { passed: 0, dirty: 0, damaged: 0, total: 0 }, // Initial accumulator
                );

                return (
                  <Form>
                    <Flex direction="column">
                      <Flex gap={4}>
                        <Flex flex={1}>
                          <EmployeeFilterableSelect
                            containerProps={{ flex: 1, mt: 3 }}
                            label="Employee"
                            name="employee"
                            labelProps={labelProps}
                          />
                        </Flex>
                        <Flex flex={1} gap={4}>
                          <Flex>
                            <FormikDatepicker
                              name="date"
                              label="Date Selection"
                              labelProps={labelProps}
                              formatInUTC
                            />
                          </Flex>
                          <Flex>
                            <FormikInput
                              name="hours"
                              label="Hours Worked"
                              labelProps={labelProps}
                              containerProps={{ flex: 1 }}
                            />
                          </Flex>
                        </Flex>
                        <Flex flex={1}>
                          <SkuMultiSelect
                            label="Select Items SKU’s"
                            labelProps={labelProps}
                            values={selectedSkus}
                            onChange={(newSelectedSkus) =>
                              handleSkuChange(
                                newSelectedSkus,
                                setFieldValue,
                                values.items,
                              )
                            }
                            isLoadingSKU={isLoadingSKU}
                            skus={skus}
                          />
                        </Flex>
                      </Flex>

                      <Grid pos="relative" mt={8}>
                        <Grid w="100%" overflow="auto" css={css}>
                          <Grid
                            overflow="auto"
                            templateRows="auto 1fr"
                            fontSize="13px">
                            <Grid
                              {...{
                                position: 'sticky',
                                top: '0',
                                w: '100%',
                                backdropFilter: 'blur(15px)',
                              }}
                              templateColumns="1.5fr 1fr 1fr 1fr 1fr 1fr"
                              gap="5px"
                              px={6}
                              py={4}
                              bg={`${pc}80`}
                              color="support.500"
                              userSelect="none"
                              fontWeight="bold"
                              borderRadius="5px">
                              <Text>Item SKU</Text>
                              <Text>Passed</Text>
                              <Text>Dirty</Text>
                              <Text>Damaged</Text>
                              <Text>Total</Text>
                              <Text>Yield</Text>
                            </Grid>
                            {values?.items?.length > 0 ? (
                              <>
                                {values?.items?.map(
                                  (item: QAItem, i: number) => {
                                    const skuName = skus.find(
                                      (sku: SKU) => sku?._id === item?.sku,
                                    )?.name;
                                    const total = Number(
                                      Number(item?.damaged || 0) +
                                        Number(item?.dirty || 0) +
                                        Number(item?.passed || 0) || 0,
                                    );
                                    const yieldValue =
                                      total > 0
                                        ? (
                                            ((item.passed || 0) / total) *
                                            100
                                          ).toFixed(2)
                                        : 0;

                                    const updateTotalAndYield = (
                                      index: number,
                                      passed: number,
                                      dirty: number,
                                      damaged: number,
                                      values: FormValues,
                                      setFieldValue: any,
                                    ) => {
                                      const total = passed + dirty + damaged;
                                      const yieldValue =
                                        total > 0 ? (passed / total) * 100 : 0;

                                      setFieldValue(
                                        `items[${index}].total`,
                                        total,
                                      );
                                      setFieldValue(
                                        `items[${index}].yield`,
                                        yieldValue,
                                      );
                                    };

                                    return (
                                      <Grid
                                        templateColumns="1.5fr 1fr 1fr 1fr 1fr 1fr"
                                        gap="5px"
                                        px={6}
                                        py={2}
                                        bg={`${pc}30`}
                                        color="support.500"
                                        userSelect="none">
                                        <Text>{skuName}</Text>
                                        <FormikInput
                                          type="number"
                                          name={`items[${i}].passed`}
                                          labelProps={labelProps}
                                          containerProps={{
                                            margin: 0,
                                          }}
                                          onInput={(
                                            e: React.ChangeEvent<HTMLInputElement>,
                                          ) => {
                                            const passedValue = Number(
                                              e.target.value || 0,
                                            );
                                            setFieldValue(
                                              `items[${i}].passed`,
                                              passedValue,
                                            );
                                            const dirtyValue = Number(
                                              values.items[i]?.dirty || 0,
                                            );
                                            const damagedValue = Number(
                                              values.items[i]?.damaged || 0,
                                            );
                                            updateTotalAndYield(
                                              i,
                                              passedValue,
                                              dirtyValue,
                                              damagedValue,
                                              values,
                                              setFieldValue,
                                            );
                                          }}
                                        />
                                        <FormikInput
                                          type="number"
                                          name={`items[${i}].dirty`}
                                          labelProps={labelProps}
                                          containerProps={{
                                            margin: 0,
                                          }}
                                          onInput={(
                                            e: React.ChangeEvent<HTMLInputElement>,
                                          ) => {
                                            const dirtyValue = Number(
                                              e.target.value || 0,
                                            );
                                            setFieldValue(
                                              `items[${i}].dirty`,
                                              dirtyValue,
                                            );
                                            const passedValue = Number(
                                              values.items[i]?.passed || 0,
                                            );
                                            const damagedValue = Number(
                                              values.items[i]?.damaged || 0,
                                            );
                                            updateTotalAndYield(
                                              i,
                                              passedValue,
                                              dirtyValue,
                                              damagedValue,
                                              values,
                                              setFieldValue,
                                            );
                                          }}
                                        />
                                        <FormikInput
                                          type="number"
                                          name={`items[${i}].damaged`}
                                          labelProps={labelProps}
                                          containerProps={{
                                            margin: 0,
                                          }}
                                          onInput={(
                                            e: React.ChangeEvent<HTMLInputElement>,
                                          ) => {
                                            const damagedValue = Number(
                                              e.target.value || 0,
                                            );
                                            setFieldValue(
                                              `items[${i}].damaged`,
                                              damagedValue,
                                            );
                                            const passedValue = Number(
                                              values.items[i]?.passed || 0,
                                            );
                                            const dirtyValue = Number(
                                              values.items[i]?.dirty || 0,
                                            );
                                            updateTotalAndYield(
                                              i,
                                              passedValue,
                                              dirtyValue,
                                              damagedValue,
                                              values,
                                              setFieldValue,
                                            );
                                          }}
                                        />
                                        <FormikInput
                                          type="text"
                                          isReadOnly
                                          name={`items[${i}].total`}
                                          labelProps={labelProps}
                                          value={String(total)}
                                          containerProps={{
                                            margin: 0,
                                          }}
                                        />
                                        <FormikInput
                                          name={`items[${i}].yield`}
                                          value={`${String(yieldValue)}%`}
                                          isReadOnly
                                          labelProps={labelProps}
                                          containerProps={{
                                            margin: 0,
                                          }}
                                        />
                                      </Grid>
                                    );
                                  },
                                )}
                                {/* Totals Row */}
                                <Grid
                                  templateColumns="1.5fr 1fr 1fr 1fr 1fr 1fr"
                                  gap="5px"
                                  px={6}
                                  py={3}
                                  roundedBottom={4}
                                  bg="green.100"
                                  color="black"
                                  fontWeight="bold"
                                  userSelect="none">
                                  <Text>Total</Text>
                                  <Text paddingLeft={'1rem'}>
                                    {totals.passed}
                                  </Text>
                                  <Text paddingLeft={'1rem'}>
                                    {totals.dirty}
                                  </Text>
                                  <Text paddingLeft={'1rem'}>
                                    {totals.damaged}
                                  </Text>
                                  <Text paddingLeft={'1rem'}>
                                    {totals.total}
                                  </Text>
                                  <Text paddingLeft={'1rem'}>
                                    {totals.total > 0
                                      ? (
                                          (totals.passed / totals.total) *
                                          100
                                        ).toFixed(2)
                                      : '0.00'}
                                    %
                                  </Text>
                                </Grid>
                              </>
                            ) : (
                              <Text
                                bg={`${pc}30`}
                                py={2}
                                my={2}
                                textAlign="center">
                                No SKU Selected
                              </Text>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>

                      <Flex mt={3}>
                        <FormikTextArea
                          labelProps={labelTextareaProps}
                          name="notes"
                          label="QA Log Notes"
                          placeholder="Your text here"
                        />
                      </Flex>

                      <Flex
                        my={12}
                        flexDirection="row"
                        justifyContent="center"
                        gap={8}>
                        <Button
                          w="200px"
                          onClick={onClose}
                          isDisabled={isLoading}
                          isLoading={isLoading}
                          variant="outline">
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          w="200px"
                          isLoading={isLoading}
                          isDisabled={isLoading}>
                          Update
                        </Button>
                      </Flex>
                    </Flex>
                  </Form>
                );
              }}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default EditQALogModalView;
